import {
  configureSearchDrawerProductCard,
  hydrateSearchDrawerProductCards,
  setCachedProduct,
} from '@theme/search-drawer-product-card';

const LIST_SELECTOR = '[data-collection-product-row-list]';
const CARD_SELECTOR = '[data-collection-product-card]';
const PAYLOAD_SELECTOR = '[data-product-payload]';
const CONFIG_SELECTOR = '[data-collection-product-row-config]';
const PAGINATION_SELECTOR = '[data-collection-product-row-pagination]';

/**
 * Parses a JSON script tag payload.
 * @param {Element|null} script
 * @returns {object|null}
 */
const parseJsonScript = (script) => {
  if (!(script instanceof HTMLScriptElement)) return null;

  try {
    return JSON.parse(script.textContent || '');
  } catch {
    return null;
  }
};

/**
 * Applies storefront config for collection product cards.
 * @param {HTMLElement} section
 */
const ensureCardConfig = (section) => {
  const configScript = section.querySelector(CONFIG_SELECTOR);
  const config = parseJsonScript(configScript) || {};

  configureSearchDrawerProductCard({
    shop: config.shop,
    storefrontAccessToken: config.storefrontAccessToken || 'c068f4855b00e4675bc5153b9564158d',
    moneyFormat: config.moneyFormat,
    currency: config.currency,
    country: config.country,
    icons: config.icons || {},
  });
};

/**
 * Returns true when the row contains product card payloads to hydrate.
 * @param {HTMLElement} section
 * @returns {boolean}
 */
const hasProductCardPayloads = (section) => {
  return Boolean(section.querySelector(`${CARD_SELECTOR} ${PAYLOAD_SELECTOR}`));
};

/**
 * Hydrates all product cards inside a collection product row.
 * @param {HTMLElement} section
 * @returns {Promise<void>}
 */
const hydrateSection = async (section) => {
  if (!hasProductCardPayloads(section)) return;

  await customElements.whenDefined('search-drawer-product-card');
  ensureCardConfig(section);

  const list = section.querySelector(LIST_SELECTOR);
  if (!(list instanceof HTMLElement)) return;

  const cards = list.querySelectorAll(CARD_SELECTOR);

  cards.forEach((item) => {
    if (!(item instanceof HTMLElement)) return;

    const card = item.querySelector('search-drawer-product-card');
    const payloadScript = item.querySelector(PAYLOAD_SELECTOR);

    if (!(card instanceof HTMLElement)) return;

    const product = parseJsonScript(payloadScript);
    if (!product?.handle) return;

    if (product.id) {
      setCachedProduct(String(product.id), product);
    }

    card.product = product;
  });

  await hydrateSearchDrawerProductCards(list);

  if (section instanceof CollectionProductRow) {
    section.refreshPagination();
  }
};

/**
 * Collection product row — hydrates cards and syncs carousel dot pagination.
 */
class CollectionProductRow extends HTMLElement {
  /** @type {AbortController|null} */
  #paginationAbort = null;

  connectedCallback() {
    void hydrateSection(this);
    this.#bindPagination();
  }

  disconnectedCallback() {
    this.#teardownPagination();
  }

  /**
   * Rebuilds pagination after list content changes (e.g. recommendations morph).
   */
  refreshPagination() {
    requestAnimationFrame(() => this.#syncPagination());
  }

  #teardownPagination() {
    this.#paginationAbort?.abort();
    this.#paginationAbort = null;
  }

  #bindPagination() {
    this.#teardownPagination();
    this.#paginationAbort = new AbortController();
    const { signal } = this.#paginationAbort;

    const onScroll = (event) => {
      if (!(event.target instanceof HTMLElement)) return;
      if (!event.target.matches(LIST_SELECTOR)) return;
      this.#syncPagination();
    };

    this.addEventListener('scroll', onScroll, { capture: true, passive: true, signal });
    window.addEventListener('resize', () => this.#syncPagination(), { passive: true, signal });

    /** @type {MutationObserver|null} */
    let listObserver = null;

    const observeList = () => {
      listObserver?.disconnect();
      const list = this.querySelector(LIST_SELECTOR);
      if (!(list instanceof HTMLElement)) return;

      listObserver = new MutationObserver(() => this.#syncPagination());
      listObserver.observe(list, { childList: true });
    };

    const rowObserver = new MutationObserver(() => {
      observeList();
      this.#syncPagination();
    });
    rowObserver.observe(this, { childList: true });

    signal.addEventListener('abort', () => {
      listObserver?.disconnect();
      rowObserver.disconnect();
    });

    observeList();
    this.refreshPagination();
  }

  /**
   * @returns {HTMLElement|null}
   */
  #ensurePaginationEl() {
    let pagination = this.querySelector(PAGINATION_SELECTOR);
    if (pagination instanceof HTMLElement) return pagination;

    const list = this.querySelector(LIST_SELECTOR);
    if (!(list instanceof HTMLElement)) return null;

    pagination = document.createElement('div');
    pagination.className = 'collection-product-row__pagination';
    pagination.setAttribute('data-collection-product-row-pagination', '');
    pagination.setAttribute('role', 'tablist');
    pagination.setAttribute('aria-label', 'Carousel pagination');
    list.after(pagination);
    return pagination;
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getSlideItems() {
    const list = this.querySelector(LIST_SELECTOR);
    if (!(list instanceof HTMLElement)) return [];

    return [...list.children].filter(
      (child) =>
        child instanceof HTMLElement &&
        child.classList.contains('collection-product-row__item') &&
        !child.classList.contains('collection-product-row__item--placeholder')
    );
  }

  #syncPagination() {
    const list = this.querySelector(LIST_SELECTOR);
    const pagination = this.#ensurePaginationEl();
    if (!(list instanceof HTMLElement) || !(pagination instanceof HTMLElement)) return;

    const items = this.#getSlideItems();
    const maxScroll = list.scrollWidth - list.clientWidth;
    const isScrollable = maxScroll > 2;

    if (!isScrollable || items.length < 2) {
      pagination.hidden = true;
      pagination.replaceChildren();
      return;
    }

    pagination.hidden = false;

    if (pagination.childElementCount !== items.length) {
      pagination.replaceChildren(
        ...items.map((item, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'collection-product-row__pagination-dot';
          button.setAttribute('aria-label', `Go to item ${index + 1}`);
          button.addEventListener('click', () => {
            item.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
          });
          return button;
        })
      );
    }

    let activeIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;
    const scrollLeft = list.scrollLeft;

    items.forEach((item, index) => {
      const distance = Math.abs(item.offsetLeft - scrollLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        activeIndex = index;
      }
    });

    [...pagination.children].forEach((dot, index) => {
      if (!(dot instanceof HTMLElement)) return;
      const isActive = index === activeIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }
}

export { hydrateSection, hasProductCardPayloads };

if (!customElements.get('collection-product-row')) {
  customElements.define('collection-product-row', CollectionProductRow);
}
